import React, { useEffect, useRef } from 'react';
import { ArrowRight, Check, Image as ImageIcon, Play, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { CampaignTypeIcon } from './CampaignTypeIcon';
import { CAMPAIGN_TITLE_ACCENT } from './campaignDialogTheme';
import { campaignTypeLabel, type CampaignButton } from '@/lib/admin/messageCampaigns';
import {
  campaignBodyBlocks,
  campaignBodyText,
  campaignRowLead,
  labelEndsWithArrow,
  renderableButtons,
  splitCampaignTitle,
  type LiveCampaign,
} from '@/lib/campaigns/portalCampaigns';
import { autoplayEmbedSrc, resolveCampaignVideo } from '@/lib/campaigns/campaignVideo';
import { usePortalCampaigns } from './usePortalCampaigns';

function AutoplayVideo({ src, onPlay }: { src: string; onPlay?: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    void Promise.resolve(el.play()).catch(() => {
    });
  }, [src]);

  return (
    <video
      ref={ref}
      src={src}
      autoPlay
      muted
      playsInline
      controls
      onPlay={onPlay}
      className="mb-3 max-h-48 w-full rounded-lg bg-black object-contain"
    />
  );
}

function MediaBlock({
  campaign,
  onPlay,
}: {
  campaign: LiveCampaign;
  onPlay?: () => void;
}) {
  if (campaign.format === 'text') return null;

  if (campaign.format === 'image' && campaign.mediaUrl) {
    return (
      <img
        src={campaign.mediaUrl}
        alt=""
        className="mb-3 max-h-48 w-full rounded-lg object-cover"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  if (campaign.format === 'video' && campaign.mediaUrl) {
    const video = resolveCampaignVideo(campaign.mediaUrl);

    if (video.kind === 'file') {
      return <AutoplayVideo src={video.src} onPlay={onPlay} />;
    }

    if (video.kind === 'embed') {
      return (
        <iframe
          src={autoplayEmbedSrc(video) ?? video.src}
          title="Campaign video"
          className="mb-3 aspect-video w-full rounded-lg border-0 bg-black"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
        />
      );
    }

  }

  return (
    <div className="mb-3 flex h-20 w-full items-center justify-center rounded-lg border border-[#E4E7EC] bg-white/70 text-[#98A2B3]">
      {campaign.format === 'video' ? <Play className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
    </div>
  );
}

function CampaignButtons({
  buttons,
  onActivate,
}: {
  buttons: CampaignButton[];
  onActivate: (button: CampaignButton) => void;
}) {
  if (buttons.length === 0) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {buttons.map((button, index) => (
        <button
          key={`${button.url}-${index}`}
          type="button"
          onClick={() => onActivate(button)}
          className="inline-flex items-center rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition hover:brightness-110"
          style={{ background: 'var(--main-color)' }}
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

const DIALOG_CTA_STYLE = { background: 'var(--main-color)' } as const;
const DIALOG_CTA_CLASS =
  'flex h-[46px] w-full items-center justify-center gap-2 rounded-xl text-[14.5px] font-semibold text-white transition hover:brightness-110 active:brightness-95';

function DialogCampaignButtons({
  buttons,
  onActivate,
  onDismiss,
}: {
  buttons: CampaignButton[];
  onActivate: (button: CampaignButton) => void;
  onDismiss: () => void;
}) {
  if (buttons.length === 0) {
    return (
      <button type="button" onClick={onDismiss} className={DIALOG_CTA_CLASS} style={DIALOG_CTA_STYLE}>
        Got it
      </button>
    );
  }
  return (
    <div className="space-y-2">
      {buttons.map((button, index) => (
        <button
          key={`${button.url}-${index}`}
          type="button"
          onClick={() => onActivate(button)}
          className={DIALOG_CTA_CLASS}
          style={DIALOG_CTA_STYLE}
        >
          {button.label}
          {!labelEndsWithArrow(button.label) && <ArrowRight className="h-[18px] w-[18px]" />}
        </button>
      ))}
    </div>
  );
}

function CampaignDialogTitle({ title }: { title: string }) {
  const { lead, accent } = splitCampaignTitle(title);
  if (!accent) return <>{lead}</>;
  return (
    <>
      {lead} —{' '}
      <span className={CAMPAIGN_TITLE_ACCENT}>{accent}</span>
    </>
  );
}

function DialogCampaignBody({ campaign }: { campaign: LiveCampaign }) {
  const blocks = campaignBodyBlocks(campaign.message, campaign.bodyFormat);
  if (blocks.length === 0) return null;

  return (
    <div data-testid="portal-campaign-body">
      {blocks.map((block, index) =>
        block.kind === 'paragraph' ? (
          <p
            key={index}
            className={cn(
              'break-words text-[14.5px] leading-relaxed text-[#6B7280]',
              index > 0 && 'mt-2',
            )}
          >
            {block.text}
          </p>
        ) : (
          <ul key={index} className={cn('space-y-2', index > 0 && 'mt-3.5')}>
            {block.items.map((item, row) => {
              return (
                <li
                  key={row}
                  className="flex items-start gap-3 rounded-2xl border border-[#EAECF0] bg-white px-3.5 py-3"
                >
                  <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 text-brand" />
                  <p className="min-w-0 flex-1 break-words text-[13.5px] leading-relaxed text-[#6B7280]">
                    {item.title && (
                      <span className="font-semibold text-[#101828]">
                        {campaignRowLead(item.title)}{' '}
                      </span>
                    )}
                    {item.text}
                  </p>
                </li>
              );
            })}
          </ul>
        ),
      )}
    </div>
  );
}

function DismissButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Dismiss"
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#6B7280] transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]',
        className,
      )}
    >
      <X className="h-4 w-4" />
    </button>
  );
}

export function PortalCampaignHost() {
  const { enabled, active, dismiss, activate, markSeen, markVideoPlayed } = usePortalCampaigns();

  const contentRef = useRef<HTMLDivElement>(null);

  const dialog = enabled && active?.placement === 'dialog_box' ? active : null;
  const corner = enabled && active?.placement === 'bottom_right' ? active : null;

  useEffect(() => {
    markSeen(dialog);
    markSeen(corner);
  }, [dialog, corner, markSeen]);

  if (!dialog && !corner) return null;

  return (
    <>
      {dialog && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) dismiss(dialog);
          }}
        >
          <DialogContent
            hideClose
            ref={contentRef}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              contentRef.current?.focus();
            }}
            className="w-[calc(100%-32px)] max-w-[520px] gap-0 overflow-hidden rounded-[24px] border-0 bg-white p-0 shadow-[0_24px_64px_rgba(16,24,40,0.18)] outline-none focus:outline-none focus-visible:outline-none sm:w-full"
            data-testid="portal-campaign-dialog"
          >
            <div className="relative px-6 pt-6 sm:px-7 sm:pt-7">
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => dismiss(dialog)}
                className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-[#98A2B3] outline-none transition hover:text-[#101828] focus-visible:text-[#101828] focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <X className="h-4 w-4" />
              </button>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-cta">
                <CampaignTypeIcon type={dialog.type} className="h-3.5 w-3.5" />
                {campaignTypeLabel(dialog)}
              </span>

              <DialogTitle className="mt-3 break-words pr-10 text-[24px] font-bold leading-[1.15] tracking-[-0.02em] text-[#101828] sm:text-[27px]">
                <CampaignDialogTitle title={dialog.title} />
              </DialogTitle>
            </div>

            <div className="max-h-[56vh] overflow-y-auto px-6 pb-1 pt-4 pr-5 sm:px-7 sm:pr-6">
              <MediaBlock campaign={dialog} onPlay={() => markVideoPlayed(dialog)} />
              <DialogCampaignBody campaign={dialog} />
            </div>

            <div className="px-6 pb-5 pt-3 sm:px-7 sm:pb-6">
              <DialogCampaignButtons
                buttons={renderableButtons(dialog.buttons)}
                onActivate={(button) => activate(dialog, button)}
                onDismiss={() => dismiss(dialog)}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {corner && (
        <div className="pointer-events-none fixed bottom-4 right-4 z-[60] w-[320px] max-w-[calc(100vw-32px)]">
          <div className="pointer-events-auto overflow-hidden rounded-xl border border-[#D0F0DC] bg-white shadow-xl">
            <div className="bg-gradient-to-r from-brand-cta to-brand px-4 py-2.5 text-white">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/80">
                  {campaignTypeLabel(corner)}
                </p>
                <button
                  type="button"
                  onClick={() => dismiss(corner)}
                  aria-label="Dismiss"
                  className="-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/15 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-0.5 break-words text-[15px] font-semibold leading-snug">
                {corner.title}
              </p>
            </div>
            <div className="p-4">
              <MediaBlock campaign={corner} onPlay={() => markVideoPlayed(corner)} />
              {campaignBodyText(corner.message, corner.bodyFormat) && (
                <p className="line-clamp-4 break-words text-[12.5px] leading-relaxed text-[#475467]">
                  {campaignBodyText(corner.message, corner.bodyFormat)}
                </p>
              )}
              <CampaignButtons
                buttons={renderableButtons(corner.buttons)}
                onActivate={(button) => activate(corner, button)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PortalCampaignHost;
